import React, { useEffect, useState } from 'react';
import { dashboardService } from 'api/services/dashboard.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { useAuth } from 'hooks/useAuth';
import BusinessDashboard from 'components/Dashboard/BusinessDashboard';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import DateFilter, { DateRange } from 'components/Filters/DateFilter';
import { getDefaultDateRange } from 'utils/helpers';
import { DASHBOARD_DATE_RANGE_DAYS } from 'utils/constants';
import { dashboardOrgId, localDayKey, showsOrgFilter } from './dashboardScope';

interface DashboardStats {
  totalCalls: number;
  incomingCalls: number;
  outgoingCalls: number;
  answeredCalls: number;
  missedCalls: number;
  /** SECONDS — see DashboardStats in dashboard.service.ts. */
  avgDurationSeconds: number;
  activeSessions: number;
  totalTransfers: number;
  recordings: number;
  // Additional metrics from reports API
  totalAiCalls?: number;
  totalAiTransferredCalls?: number;
  totalBilledTimeMs?: number;
  totalAiSessionTimeMs?: number;
  uniqueDialedNumbers?: number;
}

const Dashboard: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalCalls: 0,
    incomingCalls: 0,
    outgoingCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    avgDurationSeconds: 0,
    activeSessions: 0,
    totalTransfers: 0,
    recordings: 0
  });
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState<any[]>([]);
  const [hourlyData, setHourlyData] = useState<any[]>([]); // Store raw hourly data for heatmap
  const [pieData, setPieData] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>('all');

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange(DASHBOARD_DATE_RANGE_DAYS));
  const [chartTitle, setChartTitle] = useState('Call Volume - Last 7 Days');
  const [showDetailedChart, setShowDetailedChart] = useState(false);

  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  useEffect(() => {
    fetchDashboardData();
  }, [selectedOrgId, currentOrganization?.id, dateRange.dateFrom, dateRange.dateTo]);

  const fetchDashboardData = async () => {
    setLoading(true);

    // Determine organizationId to pass to API
    // Respects "Acting as": a business in context is always that business (dashboardScope.ts).
    const organizationId = dashboardOrgId({ currentOrganization, role: currentRole, selectedOrgId });

    console.log('📊 Dashboard: Using organizationId:', organizationId, 'Role:', currentRole);

    try {
      // Date range should always be set (either by default or user selection)
      const effectiveDateFrom = dateRange.dateFrom!;
      const effectiveDateTo = dateRange.dateTo!;

      console.log('📊 Dashboard: Using hourly reports API with dates:', {
        from: effectiveDateFrom,
        to: effectiveDateTo
      });

      // Calculate date range duration in hours
      const fromDate = new Date(effectiveDateFrom);
      const toDate = new Date(effectiveDateTo);
      const hoursDiff = Math.abs(toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60);

      // If date range is <= 24 hours, show hourly view
      // If date range is > 24 hours, show daily aggregation
      const isHourlyView = hoursDiff <= 24;

      console.log(`📊 Dashboard: Date range is ${hoursDiff.toFixed(1)} hours - Using ${isHourlyView ? 'HOURLY' : 'DAILY'} view`);

      // ALWAYS use hourly API - it has both hourly and date-wise data
      const statsRes = await dashboardService.getHourlyStats({
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
        organizationId
      });

      if (statsRes.success && statsRes.data) {
          console.log('📊 Dashboard: Raw API response:', {
            totalDataPoints: statsRes.data.dailyData?.length || 0,
            firstItem: statsRes.data.dailyData?.[0],
            lastItem: statsRes.data.dailyData?.[statsRes.data.dailyData?.length - 1],
            stats: statsRes.data.stats
          });

          setStats(statsRes.data.stats);

          // ALWAYS store raw hourly data for heatmap
          // Helper function to parse UTC timestamp to local Date object
          const parseUTCToLocal = (dateString: string): Date => {
            // Server returns UTC timestamps - ensure proper conversion to local time
            let date: Date;

            if (typeof dateString === 'string') {
              // If timestamp doesn't have timezone indicator, treat it as UTC
              if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
                // Add 'Z' to force UTC parsing for timestamps like "2025-10-07T14:00:00"
                date = new Date(dateString + 'Z');
              } else {
                // Timestamp already has timezone info
                date = new Date(dateString);
              }
            } else {
              // Handle epoch timestamps or other formats
              date = new Date(dateString);
            }

            return date; // Date object in local timezone
          };

          const rawHourlyData = statsRes.data.dailyData?.map((item: any) => {
            const localDate = parseUTCToLocal(item.date);
            return {
              date: item.date,
              hour: localDate.getHours(), // Hour in local timezone (0-23)
              minute: localDate.getMinutes(), // Minute in local timezone (0-59)
              timeString: localDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }), // e.g., "13:30", "14:00"
              calls: item.calls || 0,
              incoming: item.incoming || 0,
              outgoing: item.outgoing || 0,
              totalAiCalls: item.totalAiCalls || 0,
              totalAiTransferredCalls: item.totalAiTransferredCalls || 0,
              totalTransfers: item.totalTransfers || 0
            };
          }) || [];

          // Debug: Log timezone conversion for first few data points
          if (rawHourlyData.length > 0) {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            console.log('🌍 Timezone Conversion:', {
              userTimezone,
              samples: rawHourlyData.slice(0, 3).map(item => ({
                serverUTC: item.date,
                localTime: item.timeString,
                hour: item.hour,
                minute: item.minute
              }))
            });
          }

          setHourlyData(rawHourlyData);

          // Format data based on view type
          if (isHourlyView && statsRes.data.dailyData) {
            // For <= 24 hours: Show hour-wise breakdown with local time (24-hour format)
            const formattedData = statsRes.data.dailyData.map((item: any) => {
              const localDate = parseUTCToLocal(item.date);
              const localTimeLabel = localDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }); // e.g., "13:30", "14:00"

              return {
                day: localTimeLabel, // Use local time in 24-hour format
                date: item.date,
                calls: item.calls,
                incoming: item.incoming,
                outgoing: item.outgoing,
                missed: item.missed,
                totalAiCalls: item.totalAiCalls,
                totalAiTransferredCalls: item.totalAiTransferredCalls,
                totalTransfers: item.totalTransfers,
                totalAiSessionTimeMs: item.totalAiSessionTimeMs
              };
            });
            setDailyData(formattedData);
            setChartTitle(`Call Volume - ${effectiveDateFrom} (Hourly - 24h Format)`);
            setShowDetailedChart(true);
          } else if (statsRes.data.dailyData) {
            // For > 24 hours: Aggregate hourly data into daily buckets
            const dailyMap = new Map<string, {
              calls: number;
              incoming: number;
              outgoing: number;
              missed: number;
              totalAiCalls: number;
              totalAiTransferredCalls: number;
              totalTransfers: number;
              totalAiSessionTimeMs: number;
            }>();

            statsRes.data.dailyData.forEach((item: any) => {
              // Extract date (YYYY-MM-DD) from the timestamp
              // item.date can be ISO string, timestamp, or Date object
              let dateKey = '';

              // The viewer's (IST) calendar day, not the UTC one: splitting the UTC
              // timestamp put every call between 00:00 and 05:30 IST on the day before.
              if (item.date) {
                dateKey = localDayKey(item.date);
              }

              if (dateKey) {
                const existing = dailyMap.get(dateKey) || {
                  calls: 0,
                  incoming: 0,
                  outgoing: 0,
                  missed: 0,
                  totalAiCalls: 0,
                  totalAiTransferredCalls: 0,
                  totalTransfers: 0,
                  totalAiSessionTimeMs: 0
                };
                existing.calls += item.calls || 0;
                existing.incoming += item.incoming || 0;
                existing.outgoing += item.outgoing || 0;
                existing.missed += item.missed || 0;
                existing.totalAiCalls += item.totalAiCalls || 0;
                existing.totalAiTransferredCalls += item.totalAiTransferredCalls || 0;
                existing.totalTransfers += item.totalTransfers || 0;
                existing.totalAiSessionTimeMs += item.totalAiSessionTimeMs || 0;
                dailyMap.set(dateKey, existing);
              } else {
                console.warn('📊 Dashboard: Could not extract date from item:', item);
              }
            });

            // Convert to array and sort by date
            const aggregatedData = Array.from(dailyMap.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, counts]) => ({
                day: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                date: date,
                calls: counts.calls,
                incoming: counts.incoming,
                outgoing: counts.outgoing,
                missed: counts.missed,
                totalAiCalls: counts.totalAiCalls,
                totalAiTransferredCalls: counts.totalAiTransferredCalls,
                totalTransfers: counts.totalTransfers,
                totalAiSessionTimeMs: counts.totalAiSessionTimeMs
              }));

            console.log('📊 Dashboard: Daily aggregation result:', {
              hourlyDataPoints: statsRes.data.dailyData?.length || 0,
              aggregatedDays: aggregatedData.length,
              dateRange: aggregatedData.map(d => d.day).join(', '),
              totalCalls: aggregatedData.reduce((sum, d) => sum + d.calls, 0)
            });

            setDailyData(aggregatedData);
            const fromDateStr = effectiveDateFrom ? new Date(effectiveDateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            const toDateStr = effectiveDateTo ? new Date(effectiveDateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            setChartTitle(`Call Volume - ${fromDateStr} to ${toDateStr} (Daily)`);
            setShowDetailedChart(true);
          }

          setPieData(statsRes.data.pieData || []);

          console.log('📊 Dashboard: ✅ Successfully loaded from HOURLY REPORTS API:', {
            view: isHourlyView ? 'hourly' : 'daily-aggregated',
            dateRange: `${hoursDiff.toFixed(1)} hours`,
            totalCalls: statsRes.data.stats.totalCalls,
            totalAiCalls: statsRes.data.stats.totalAiCalls,
            chartPoints: statsRes.data.dailyData?.length || 0,
            hasBreakdown: statsRes.data.dailyData?.[0]?.incoming !== undefined
          });
      } else {
        console.error('📊 Dashboard: Hourly reports API returned no data:', statsRes.error);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  // Render filter component for master_admin, super_admin, service_admin, and service_user
  const renderOrganizationFilter = () => {
    if (!showsOrgFilter({ currentOrganization, role: currentRole, selectedOrgId })) {
      return null;
    }

    return (
      <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <OrganizationFilter
          value={selectedOrgId === 'all' ? '' : selectedOrgId}
          onChange={(value) => setSelectedOrgId(value || 'all')}
          className="w-full md:w-96"
          allowServiceProviderSelection={true}
        />
      </div>
    );
  };

  // All users → BusinessDashboard (unified dashboard for all roles)
  return (
    <>
      {/* No heading here on purpose. BusinessDashboard, rendered below, carries
          the page's own top-level heading — so this page is already titled, it
          just is not titled in THIS file. Checking the file alone said
          otherwise, and acting on that put two headings on the screen, which is
          the fault that removing the bar's title was fixing. */}
      {renderOrganizationFilter()}
      <DateFilter
        dateRange={dateRange}
        onChange={setDateRange}
        className="mb-4"
      />
      <BusinessDashboard
        stats={stats}
        dailyData={dailyData}
        hourlyData={hourlyData}
        pieData={pieData}
        chartTitle={chartTitle}
        showDetailedChart={showDetailedChart}
      />
    </>
  );
};

export default Dashboard;